/**
 * AppShell.tsx — Rivmoon Console 应用壳
 *
 * 移植自原型 design/Novel-admin/src/layout.jsx（Sidebar / Topbar / AppSwitcher）
 * 与 design/Novel-admin/src/app.jsx（壳逻辑 / TweaksPanel）。
 *
 * 职责：左侧三组导航 + App 切换器 + 底部用户/设置/退出；顶栏折叠/面包屑/搜索⌘K/在线数/主题切换/通知；
 * ⌘K 弹出 AppSwitcher、Esc 关闭；TweaksPanel（主题/强调色/密度/侧边栏）。内容区用 react-router <Outlet/> 渲染。
 * 任意 TanStack Query 在途时，在顶栏下沿绝对叠加全局进度条 TopBar（对应原型 app.jsx 的
 * `{loading && <TopBar/>}`）；进度条脱离 flex 流，toggle 不推动内容、零布局位移。
 *
 * 移植铁律：JSX / className / inline-style 与原型逐字一致；
 * window.X 全局 → ESM import；JS → TS（加严格类型）；路由从 hash-route 改为 react-router。
 */

import type { AdminRoleLiteral } from '@novel-admin/shared';
import { useIsFetching } from '@tanstack/react-query';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppScope, useApplyAppearance, useResolvedTheme } from '~/app/app-scope-store';
import type { ThemePref } from '~/app/app-scope-store';
import { useApps, useSysConfig } from '~/data/queries';
import type { AppRow } from '~/data/queries';
import { fmtCountry } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { PageSkeleton, TopBar } from '~/ui/Skeleton';

// ---------- 导航分组（照原型 layout.jsx 的 NAV_GROUPS，补 paywall）----------

interface NavItem {
  /** id 即路由 path 片段：dashboard→'/'，其余→'/'+id */
  id: string;
  label: string;
  icon: string;
  badge?: string | null;
  /** 仅这些角色可见（UX 隐藏；后端 requireRole 才是权威）；缺省 = 全部角色 */
  roles?: AdminRoleLiteral[];
}
interface NavGroup {
  section: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    section: '概览',
    items: [{ id: 'dashboard', label: '数据总览', icon: 'IconHome', badge: null }],
  },
  {
    // 数据中台（对照原型 layout.jsx「数据中台」组；/metrics 根路由重定向到本组首项）
    section: '数据中台',
    items: [
      { id: 'metrics/engagement', label: '内容消费', icon: 'IconEye', badge: null },
      { id: 'metrics/monetization', label: '商业化', icon: 'IconCoin', badge: null },
      { id: 'metrics/rewarded-ads', label: 'IAA 激励广告统计', icon: 'IconChart', badge: null },
      {
        id: 'metrics/activation-conversion',
        label: '激活转化看板',
        icon: 'IconSparkles',
        badge: null,
      },
      { id: 'metrics/paywall-conversion', label: '付费转化看板', icon: 'IconWallet', badge: null },
      { id: 'metrics/web-funnels', label: 'Web 行为漏斗', icon: 'IconGlobe', badge: null },
      { id: 'metrics/first-payment-cohorts', label: '付费 cohort', icon: 'IconCoin', badge: null },
      { id: 'metrics/retention-cohorts', label: '用户留存', icon: 'IconUsers', badge: null },
      { id: 'metrics/ltv', label: 'LTV 队列', icon: 'IconLayers', badge: null },
      { id: 'metrics/usd-coverage', label: 'USD 来源覆盖', icon: 'IconLayers', badge: null },
      { id: 'metrics/diagnosis', label: '诊断下钻', icon: 'IconSearch', badge: null },
    ],
  },
  {
    section: '内容',
    items: [
      { id: 'apps', label: 'App 管理', icon: 'IconApps', badge: null }, // 去假：原编造 '7'，待接真 App 数
      { id: 'books', label: '书籍管理', icon: 'IconBook', badge: null },
      { id: 'paywall', label: '支付页面', icon: 'IconWallet', badge: null },
      { id: 'ops', label: '运营管理', icon: 'IconLayout', badge: null },
    ],
  },
  {
    section: '投放管理',
    items: [
      { id: 'landing/projects', label: '项目管理', icon: 'IconLayers', badge: null },
      { id: 'landing/settings', label: '投放设置', icon: 'IconCog', badge: null },
    ],
  },
  {
    section: '用户与营收',
    items: [
      { id: 'users', label: '用户管理', icon: 'IconUsers', badge: null },
      { id: 'finance', label: '财务管理', icon: 'IconWallet', badge: null },
      { id: 'feedback', label: '用户反馈', icon: 'IconMsg', badge: null }, // 去假：原编造 '12'，待接真待回复数（需后端）
      { id: 'push', label: '推送管理', icon: 'IconBell', badge: null },
    ],
  },
  {
    // 系统（对照原型 layout.jsx「系统」组）：服务器监控仅 root / admin（日志 cause 含内部错误信息）
    section: '系统',
    items: [
      {
        id: 'server-monitor',
        label: '服务器监控',
        icon: 'IconMonitor',
        badge: null,
        roles: ['root', 'admin'],
      },
    ],
  },
];

/** 按角色过滤导航：带 roles 的项只给对应角色看；整组为空则不渲染分组标题 */
function visibleNavGroups(role: AdminRoleLiteral | undefined): NavGroup[] {
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(
      (item) => !item.roles || (role !== undefined && item.roles.includes(role)),
    ),
  })).filter((g) => g.items.length > 0);
}

/** id → 路由 path：dashboard 映射到根，其余前缀 '/' */
function navIdToPath(id: string): string {
  return id === 'dashboard' ? '/' : `/${id}`;
}

/**
 * sys-config 首屏 gate 的等待上界（ms）—— 到点放行、回落内置兜底语言名表。
 * 取值只需「明显长于一次正常 RTT、又短到不像卡死」；正常路径根本碰不到它。
 */
const SYS_CONFIG_GATE_MAX_MS = 3_000;

/** 所有 nav id（含带斜杠复合 id 如 metrics/diagnosis），pathToNavId 精确匹配用 */
const NAV_ITEM_IDS = new Set(NAV_GROUPS.flatMap((g) => g.items.map((i) => i.id)));

/** 由 pathname 反推当前 active 的 nav id（用于高亮）。'/' → dashboard；
 *  前两段命中复合 nav id（如 /metrics/diagnosis → metrics/diagnosis）优先，否则退回首段（既有页行为不变） */
function pathToNavId(pathname: string): string {
  if (pathname === '/') return 'dashboard';
  const segs = pathname.split('/').filter(Boolean);
  const composite = segs.slice(0, 2).join('/');
  if (NAV_ITEM_IDS.has(composite)) return composite;
  return segs[0] || 'dashboard';
}

// ---------- 聚合 App（appId==='all' 时的当前 App 对象）----------

interface CurrentApp {
  id: string;
  name: string;
  icon: string;
  color?: string;
  platform: string;
  market: string[];
  status?: string;
  lang?: string[];
}

const ALL_APP: CurrentApp = {
  id: 'all',
  name: '全部 App',
  icon: '全',
  color: 'var(--bg-inset)',
  platform: '聚合',
  market: ['EU'],
};

// 页面 label 映射（也用于面包屑），照原型 app.jsx 的 PAGE，补 paywall / system-config
const PAGE_LABEL: Record<string, string> = {
  dashboard: '数据总览',
  apps: 'App 管理',
  books: '书籍管理',
  paywall: '支付页面',
  finance: '财务管理',
  users: '用户管理',
  feedback: '用户反馈',
  push: '推送管理',
  ops: '运营管理',
  landing: '投放管理',
  'landing/projects': '项目管理',
  'landing/settings': '投放设置',
  settings: '系统设置',
  'system-config': '系统配置',
  profile: '个人资料',
  'design-system': '设计系统',
  'metrics/engagement': '内容消费',
  'metrics/monetization': '商业化',
  'metrics/rewarded-ads': 'IAA 激励广告统计',
  'metrics/activation-conversion': '激活转化看板',
  'metrics/paywall-conversion': '付费转化看板',
  'metrics/web-funnels': 'Web 行为漏斗',
  'metrics/first-payment-cohorts': '付费 cohort',
  'metrics/retention-cohorts': '用户留存',
  'metrics/ltv': 'LTV 队列',
  'metrics/usd-coverage': 'USD 来源覆盖',
  'metrics/diagnosis': '诊断下钻',
  'server-monitor': '服务器监控',
};

const ACCENT_OPTIONS = [
  { label: 'Warm Amber', hue: 50 },
  { label: 'Forest', hue: 152 },
  { label: 'Indigo', hue: 245 },
  { label: 'Plum', hue: 320 },
];

// ---------- NavIcon ----------

function NavIcon({ name, ...rest }: { name: string; size?: number }) {
  const Cmp =
    (Icons as Record<string, React.ComponentType<{ size?: number }>>)[name] || Icons.IconCircle;
  return <Cmp {...rest} />;
}

// ---------- Sidebar ----------

interface SidebarProps {
  current: string;
  onNav: (id: string) => void;
  collapsed: boolean;
  app: CurrentApp;
  apps: AppRow[];
  onAppSwitcherOpen: () => void;
}

function Sidebar({ current, onNav, collapsed, app, apps, onAppSwitcherOpen }: SidebarProps) {
  // 侧栏底部账户区展示真实登录运营员（取自 auth-store，非写死占位）：
  // 昵称优先、回退邮箱；头像首字母由昵称/邮箱首字符大写派生。
  const authUser = useAuthStore((s) => s.user);
  const accountName = authUser?.nickname || authUser?.email || '未登录';
  const accountEmail = authUser?.email ?? '';
  const accountInitial = (authUser?.nickname || authUser?.email || '?').charAt(0).toUpperCase();
  return (
    <aside
      className="sidebar"
      style={{
        width: collapsed ? 56 : 220,
        transition: 'width 0.18s ease',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {/* Brand */}
      <div
        style={{
          padding: collapsed ? '16px 12px 12px' : '16px 14px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: 'linear-gradient(135deg, var(--fg-strong), oklch(0.32 0.04 250))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--bg-elev)',
            flexShrink: 0,
          }}
        >
          {/* Subtle wordmark */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 4v16M5 4l14 16M19 4v16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        {!collapsed && (
          <div style={{ minWidth: 0, lineHeight: 1.1 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, letterSpacing: '-0.01em' }}>
              Rivmoon Console
            </div>
            <div className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
              v2026.05 · EU
            </div>
          </div>
        )}
      </div>

      {/* App switcher */}
      {!collapsed && (
        <div style={{ padding: '0 10px 10px' }}>
          <button
            onClick={onAppSwitcherOpen}
            className="focusable"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 8px',
              borderRadius: 'var(--radius)',
              background: 'var(--bg-elev)',
              border: '1px solid var(--border)',
              cursor: 'pointer',
              font: 'inherit',
              color: 'var(--fg)',
              boxShadow: 'var(--shadow-xs)',
            }}
          >
            {app.id === 'all' ? (
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 5,
                  background: 'var(--bg-inset)',
                  border: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icons.IconLayers size={12} />
              </div>
            ) : (
              <div
                className="app-icon"
                style={{ width: 22, height: 22, fontSize: 10, background: app.color }}
              >
                {app.icon}
              </div>
            )}
            <div style={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {app.name}
              </div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                {app.id === 'all'
                  ? `${apps.length} apps · 全平台`
                  : `${app.platform} · ${app.market[0] ? fmtCountry(app.market[0]) : ''}${
                      app.market.length > 1 ? ` +${app.market.length - 1}` : ''
                    }`}
              </div>
            </div>
            <Icons.IconChevDown size={13} style={{ color: 'var(--fg-muted)' }} />
          </button>
        </div>
      )}

      <nav style={{ flex: 1, padding: '0 6px', overflow: 'auto' }}>
        {visibleNavGroups(authUser?.role).map((g) => (
          <div key={g.section}>
            {!collapsed && <div className="nav-section">{g.section}</div>}
            {collapsed && <div style={{ height: 12 }} />}
            {g.items.map((item) => {
              const active = current === item.id;
              return (
                <div
                  key={item.id}
                  className={`nav-item${active ? ' active' : ''}`}
                  onClick={() => onNav(item.id)}
                  style={{
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    padding: collapsed ? '8px 0' : undefined,
                  }}
                  title={collapsed ? item.label : undefined}
                >
                  <NavIcon name={item.icon} size={15} />
                  {!collapsed && <span style={{ flex: 1 }}>{item.label}</span>}
                  {!collapsed && item.badge && (
                    <span className="chip chip-accent" style={{ padding: '0 5px', fontSize: 10 }}>
                      {item.badge}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      <div style={{ borderTop: '1px solid var(--sidebar-border)', padding: 8 }}>
        <div
          className={`nav-item${current === 'settings' ? ' active' : ''}`}
          onClick={() => onNav('settings')}
          style={{
            padding: collapsed ? '8px 0' : undefined,
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
          title={collapsed ? '系统设置' : undefined}
        >
          <Icons.IconCog size={15} />
          {!collapsed && <span style={{ flex: 1 }}>系统设置</span>}
        </div>
        {!collapsed && (
          <div
            onClick={() => onNav('profile')}
            className={`nav-item${current === 'profile' ? ' active' : ''}`}
            style={{ padding: '6px 6px', marginTop: 4, gap: 8, cursor: 'pointer' }}
          >
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: 999,
                background: 'linear-gradient(135deg, oklch(0.65 0.12 250), oklch(0.55 0.15 320))',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {accountInitial}
            </div>
            <div style={{ flex: 1, minWidth: 0, lineHeight: 1.1 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'var(--fg)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {accountName}
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  color: 'var(--fg-faint)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {accountEmail}
              </div>
            </div>
            <LogoutButton />
          </div>
        )}
        {collapsed && (
          <div
            onClick={() => onNav('profile')}
            style={{
              padding: '8px 0',
              display: 'flex',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            title="个人资料"
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 999,
                background: 'linear-gradient(135deg, oklch(0.65 0.12 250), oklch(0.55 0.15 320))',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {accountInitial}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

// ---------- LogoutButton（退出跳 /auth/login）----------

function LogoutButton() {
  const navigate = useNavigate();
  return (
    <button
      className="btn btn-ghost btn-icon"
      style={{ width: 22, height: 22 }}
      title="退出"
      onClick={(e) => {
        e.stopPropagation();
        navigate('/auth/login');
      }}
    >
      <Icons.IconLogout size={12} />
    </button>
  );
}

// ---------- AppSwitcher（⌘K 弹窗）----------

interface AppSwitcherProps {
  apps: AppRow[];
  current: string;
  onSelect: (id: string) => void;
  onClose: () => void;
}

interface SwitcherRow {
  id: string;
  name: string;
  icon: string;
  color?: string;
  platform: string;
  market: string[];
  aggregated?: boolean;
  status?: string;
  lang?: string[];
}

function AppSwitcher({ apps, current, onSelect, onClose }: AppSwitcherProps) {
  const [query, setQuery] = useState('');
  const rows: SwitcherRow[] = [
    {
      id: 'all',
      name: '全部 App',
      icon: '全',
      color: 'var(--bg-inset)',
      platform: '聚合',
      market: ['EU'],
      aggregated: true,
    },
    ...apps,
  ];
  const filtered = rows.filter((a) => a.name.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" style={{ width: 480 }} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            padding: '12px 14px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Icons.IconSearch size={15} style={{ color: 'var(--fg-muted)' }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索 App…"
            style={{
              flex: 1,
              border: 0,
              outline: 0,
              background: 'transparent',
              color: 'var(--fg)',
              font: 'inherit',
              fontSize: 14,
            }}
          />
          <span className="kbd">ESC</span>
        </div>
        <div style={{ maxHeight: 440, overflow: 'auto', padding: 6 }}>
          {filtered.map((a) => {
            const isCurrent = current === a.id;
            return (
              <div
                key={a.id}
                onClick={() => {
                  onSelect(a.id);
                  onClose();
                }}
                className="focusable"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: isCurrent ? 'var(--bg-subtle)' : 'transparent',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-subtle)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background = isCurrent
                    ? 'var(--bg-subtle)'
                    : 'transparent';
                }}
              >
                {a.aggregated ? (
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 7,
                      background: 'var(--bg-inset)',
                      border: '1px solid var(--border)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icons.IconLayers size={15} />
                  </div>
                ) : (
                  <div
                    className="app-icon"
                    style={{
                      width: 32,
                      height: 32,
                      fontSize: 13,
                      background: a.color,
                      borderRadius: 7,
                    }}
                  >
                    {a.icon}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 13.5 }}>{a.name}</div>
                  <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                    {a.aggregated
                      ? '聚合视图，全部 App 数据合并展示'
                      : `${a.platform} · ${a.market.map((m) => fmtCountry(m)).join(' · ')} · ${a.lang?.length || 0} 语言`}
                  </div>
                </div>
                {!a.aggregated && (
                  <span
                    className={`chip ${a.status === 'active' ? 'chip-pos' : 'chip-warn'}`}
                    style={{ fontSize: 10 }}
                  >
                    {a.status === 'active' ? '运营中' : '已暂停'}
                  </span>
                )}
                {isCurrent && <Icons.IconCheck size={14} style={{ color: 'var(--accent)' }} />}
              </div>
            );
          })}
        </div>
        <div
          style={{
            borderTop: '1px solid var(--border)',
            padding: '8px 12px',
            display: 'flex',
            justifyContent: 'space-between',
            color: 'var(--fg-faint)',
            fontSize: 11,
          }}
        >
          <span>↑↓ 切换 · ↵ 选择</span>
          <span>
            <span className="kbd">⌘</span> <span className="kbd">K</span> 全局命令
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------- Topbar ----------

interface TopbarProps {
  breadcrumb: string[];
  onToggleSidebar: () => void;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  onCommand: () => void;
}

function Topbar({ breadcrumb, onToggleSidebar, theme, onToggleTheme, onCommand }: TopbarProps) {
  return (
    <header
      style={{
        height: 48,
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-elev)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        gap: 12,
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 30,
      }}
    >
      <button className="btn btn-ghost btn-icon" onClick={onToggleSidebar} title="折叠侧边栏">
        <Icons.IconList size={15} />
      </button>

      {/* Breadcrumb */}
      <div className="hstack" style={{ gap: 6, fontSize: 12.5, color: 'var(--fg-muted)' }}>
        {breadcrumb.map((b, i) => (
          <Fragment key={i}>
            {i > 0 && <Icons.IconChevRight size={11} style={{ color: 'var(--fg-faint)' }} />}
            <span
              style={{
                color: i === breadcrumb.length - 1 ? 'var(--fg-strong)' : 'var(--fg-muted)',
                fontWeight: i === breadcrumb.length - 1 ? 500 : 400,
              }}
            >
              {b}
            </span>
          </Fragment>
        ))}
      </div>

      <div style={{ flex: 1 }} />

      {/* Search */}
      <button
        onClick={onCommand}
        className="focusable"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '0 10px',
          height: 30,
          width: 230,
          border: '1px solid var(--border)',
          background: 'var(--bg-subtle)',
          borderRadius: 'var(--radius)',
          cursor: 'pointer',
          font: 'inherit',
          color: 'var(--fg-muted)',
          fontSize: 12.5,
        }}
      >
        <Icons.IconSearch size={13} />
        <span style={{ flex: 1, textAlign: 'left' }}>搜索书籍、用户、订单…</span>
        <span className="kbd">⌘K</span>
      </button>

      {/* 实时在线（ADM-DASH-S007）后端端点未实现 → 不显示编造数字，降级为「未接入」诚实占位（去假为先 2026-06-21）*/}
      <div
        className="hstack"
        title="实时在线数据未接入（ADM-DASH-S007 待后端）"
        style={{
          gap: 6,
          padding: '0 10px',
          height: 28,
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          background: 'var(--bg-subtle)',
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--fg-muted)' }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          在线 —
        </span>
      </div>

      <button className="btn btn-ghost btn-icon" onClick={onToggleTheme} title="切换主题">
        {theme === 'dark' ? <Icons.IconSun size={15} /> : <Icons.IconMoon size={15} />}
      </button>
      {/* 通知未读红点无数据源（通知中心未接入）→ 移除编造的未读提示（去假为先）*/}
      <button className="btn btn-ghost btn-icon">
        <Icons.IconBell size={15} />
      </button>
    </header>
  );
}

// ---------- TweaksPanel（移植 app.jsx 的 TweaksPanelInline）----------

interface TweaksPanelProps {
  theme: ThemePref;
  density: 'compact' | 'standard' | 'comfortable';
  accent: number;
  sidebarCollapsed: boolean;
  setTweak: (
    patch: Partial<{
      theme: ThemePref;
      density: 'compact' | 'standard' | 'comfortable';
      accent: number;
      sidebarCollapsed: boolean;
    }>,
  ) => void;
  onClose: () => void;
}

function TweaksPanel({
  theme,
  density,
  accent,
  sidebarCollapsed,
  setTweak,
  onClose,
}: TweaksPanelProps) {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: 280,
        zIndex: 70,
        background: 'var(--bg-elev)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)',
        animation: 'slide-up 0.18s ease',
      }}
    >
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div className="hstack" style={{ gap: 8 }}>
          <Icons.IconSparkles size={13} style={{ color: 'var(--accent)' }} />
          <span style={{ fontWeight: 500, fontSize: 12.5 }}>Tweaks</span>
        </div>
        <button
          className="btn btn-ghost btn-icon"
          style={{ width: 24, height: 24 }}
          onClick={onClose}
        >
          <Icons.IconX size={13} />
        </button>
      </div>
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div className="label" style={{ marginBottom: 6 }}>
            主题
          </div>
          <div className="seg" style={{ width: '100%' }}>
            <button
              className={theme === 'light' ? 'on' : ''}
              onClick={() => setTweak({ theme: 'light' })}
              style={{ flex: 1 }}
            >
              <Icons.IconSun size={11} style={{ marginRight: 4 }} />
              浅色
            </button>
            <button
              className={theme === 'dark' ? 'on' : ''}
              onClick={() => setTweak({ theme: 'dark' })}
              style={{ flex: 1 }}
            >
              <Icons.IconMoon size={11} style={{ marginRight: 4 }} />
              深色
            </button>
            <button
              className={theme === 'system' ? 'on' : ''}
              onClick={() => setTweak({ theme: 'system' })}
              style={{ flex: 1 }}
            >
              <Icons.IconMonitor size={11} style={{ marginRight: 4 }} />
              系统
            </button>
          </div>
        </div>
        <div>
          <div className="label" style={{ marginBottom: 6 }}>
            强调色
          </div>
          <div className="hstack" style={{ gap: 6 }}>
            {ACCENT_OPTIONS.map((o) => (
              <button
                key={o.hue}
                onClick={() => setTweak({ accent: o.hue })}
                style={{
                  flex: 1,
                  height: 28,
                  borderRadius: 'var(--radius)',
                  border:
                    accent === o.hue ? '2px solid var(--fg-strong)' : '1px solid var(--border)',
                  background: `oklch(0.68 0.17 ${o.hue})`,
                  cursor: 'pointer',
                  padding: 0,
                  position: 'relative',
                }}
                title={o.label}
              >
                {accent === o.hue && (
                  <Icons.IconCheck
                    size={12}
                    style={{ color: 'white', position: 'absolute', inset: 0, margin: 'auto' }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="label" style={{ marginBottom: 6 }}>
            信息密度
          </div>
          <div className="seg" style={{ width: '100%' }}>
            <button
              className={density === 'compact' ? 'on' : ''}
              onClick={() => setTweak({ density: 'compact' })}
              style={{ flex: 1 }}
            >
              紧凑
            </button>
            <button
              className={density === 'standard' ? 'on' : ''}
              onClick={() => setTweak({ density: 'standard' })}
              style={{ flex: 1 }}
            >
              标准
            </button>
            <button
              className={density === 'comfortable' ? 'on' : ''}
              onClick={() => setTweak({ density: 'comfortable' })}
              style={{ flex: 1 }}
            >
              宽松
            </button>
          </div>
        </div>
        <div>
          <div className="label" style={{ marginBottom: 6 }}>
            侧边栏
          </div>
          <div className="seg" style={{ width: '100%' }}>
            <button
              className={!sidebarCollapsed ? 'on' : ''}
              onClick={() => setTweak({ sidebarCollapsed: false })}
              style={{ flex: 1 }}
            >
              展开
            </button>
            <button
              className={sidebarCollapsed ? 'on' : ''}
              onClick={() => setTweak({ sidebarCollapsed: true })}
              style={{ flex: 1 }}
            >
              折叠
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- AppShell（壳；合并 app.jsx 的 App）----------

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();

  // 全局态走 app-scope-store（替代原型 useTweaks + 本地 appId state）
  const appId = useAppScope((s) => s.appId);
  const theme = useAppScope((s) => s.theme);
  const density = useAppScope((s) => s.density);
  const accent = useAppScope((s) => s.accent);
  const sidebarCollapsed = useAppScope((s) => s.sidebarCollapsed);
  const setAppId = useAppScope((s) => s.setAppId);
  const setTweak = useAppScope((s) => s.setTweak);

  // 把 theme / density / accent 同步到 documentElement（data-theme / data-density / --accent-h）
  useApplyAppearance();

  // theme==='system' 时解析为实际明暗，供顶栏图标 / 切换逻辑使用
  const resolvedTheme = useResolvedTheme();

  const { data: apps } = useApps();
  /**
   * 语言名 SSOT 的**唯一常驻订阅点**（#594 ④）。做两件事，缺一不可：
   *
   * ① **触发 fetch**。`langName` / `fmtLang` 读的是 `~/lib/format` 的模块级运行时表，只有
   *    `useSysConfig` 的 queryFn 会往里注册。而全站 58 个语言名渲染站点里 53 个所在的组件**不**订阅
   *    它——冷启动直接进 `/users` 或整个 `landing/**` 域时，这次请求根本不会发出，那些页面永远读
   *    内置兜底表（DB 新增语言即渲染空名，且此后没有任何东西来纠正）。
   * ② **让子树在注册之后才挂载**（见下方内容区的 gate）。只订阅是不够的：route element 在
   *    `createBrowserRouter` 时创建一次、引用恒定，AppShell 自身重渲染时 `<Outlet/>` 拿回同一个
   *    element 对象，React 直接 `bailoutOnAlreadyFinishedWork` 跳过整棵子树 —— 实测 AppShell 渲染
   *    2→3 而页面渲染 0 次、文案停在裸 `TR`。换 `Component=` 写法或加 StrictMode 都一样。
   *
   * 不放 router loader：loader 跑在 `RequireAuth` 之前，此时落盘的 accessToken 可能缺失或已过期，
   * 请求 401 → 无 errorElement 时整站被 react-router 默认错误页接管，连登录页都进不去。
   */
  const { isLoading: sysConfigFirstLoad } = useSysConfig();
  /**
   * **一次性闸**：放行之后永不重新进入。
   *
   * 直接用 `isLoading` 会把 gate 变成会话期常驻条件 —— sys-config 失败后子页面自己的
   * `useSysConfig` 订阅会触发 refetch，把 gate 重新关上，子树 mount→unmount 反复循环
   * （实测：子组件渲染 3 次后最终不在文档里），用户正在看的页面被替换成骨架、滚动 / 弹窗 /
   * 表单态全丢，还会持续冲击后端。语言名表只需要**首屏之前**注册一次，之后 refetch 只是刷新
   * 那张表、不需要再挡任何东西。
   *
   * 渲染期条件 setState 是 React 文档认可的「派生态」写法（只改自身 state、幂等、立即重渲染
   * 不提交中间结果）。
   */
  const [sysConfigBooted, setSysConfigBooted] = useState(false);
  if (!sysConfigFirstLoad && !sysConfigBooted) setSysConfigBooted(true);
  /**
   * **上界**：语言名是 cosmetic 标签，不该成为全站可用性依赖。请求挂起（不是快速失败）时
   * query 要等到 30s 超时 × retry 才 settle —— 那期间整个内容区停在骨架上。到点即放行，
   * 回落 `~/lib/format` 的内置兜底表（= 本改动之前的表现）。正常路径一个 RTT 就 settle，
   * 这个定时器碰不到。
   */
  useEffect(() => {
    const t = setTimeout(() => setSysConfigBooted(true), SYS_CONFIG_GATE_MAX_MS);
    return () => clearTimeout(t);
  }, []);
  const sysConfigLoading = !sysConfigBooted;

  // 全局数据在途指示：任意 TanStack Query fetch 进行中 → 顶栏下方叠加 TopBar 进度条
  // （实现层用真实 useIsFetching 计数门控，替代原型 app.jsx 的假定时器 loading）
  const isFetching = useIsFetching();

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [tweaksOpen, setTweaksOpen] = useState(false);

  // Edit-mode protocol（与设计原型一致：父窗激活 / 关闭 Tweaks 面板）
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data;
      if (!d || !d.type) return;
      if (d.type === '__activate_edit_mode') setTweaksOpen(true);
      else if (d.type === '__deactivate_edit_mode') setTweaksOpen(false);
    };
    window.addEventListener('message', onMsg);
    try {
      window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    } catch {
      /* 顶层窗口无父级，忽略 */
    }
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Global cmd+k → switcher（Esc 由 AppSwitcher 内部处理）
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSwitcherOpen(true);
      }
    };
    // 页面内「单 App…」入口（如数据中台顶栏）经此事件请求打开全局 AppSwitcher（等价 ⌘K）
    const open = () => setSwitcherOpen(true);
    window.addEventListener('keydown', h);
    window.addEventListener('na:open-app-switcher', open);
    return () => {
      window.removeEventListener('keydown', h);
      window.removeEventListener('na:open-app-switcher', open);
    };
  }, []);

  // 解析当前 App：appId==='all' → 聚合对象；否则查 apps
  const currentApp: CurrentApp =
    appId === 'all'
      ? ALL_APP
      : ((apps.find((a) => a.id === appId) as CurrentApp | undefined) ?? apps[0] ?? ALL_APP);

  const current = pathToNavId(location.pathname);
  const goto = useCallback((id: string) => navigate(navIdToPath(id)), [navigate]);

  const breadcrumb = [
    'Rivmoon Console',
    currentApp.id === 'all' ? '全部 App' : currentApp.name,
    PAGE_LABEL[current] || current,
  ];

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>
      <Sidebar
        current={current}
        onNav={goto}
        collapsed={sidebarCollapsed}
        app={currentApp}
        apps={apps}
        onAppSwitcherOpen={() => setSwitcherOpen(true)}
      />
      {/* position:relative 让进度条以 <main> 为定位锚点，绝对叠加不参与 flex 流 */}
      <main
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Topbar
          breadcrumb={breadcrumb}
          onToggleSidebar={() => setTweak({ sidebarCollapsed: !sidebarCollapsed })}
          theme={resolvedTheme}
          onToggleTheme={() => setTweak({ theme: resolvedTheme === 'dark' ? 'light' : 'dark' })}
          onCommand={() => setSwitcherOpen(true)}
        />
        {/* 全局数据在途时的顶部细进度条（对应原型 app.jsx `{loading && <TopBar/>}`）：
            绝对定位贴在顶栏下沿（top=48 顶栏高），脱离 flex 流 —— 任意后台 refetch
            导致的进度条 toggle 都不会推动内容、零布局位移 */}
        {isFetching > 0 && (
          <TopBar
            style={{
              position: 'absolute',
              top: 48,
              left: 0,
              right: 0,
              width: 'auto',
              zIndex: 29,
            }}
          />
        )}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {/* 只 gate 内容区：侧栏 / 顶栏只用 fmtCountry，不读语言名表，没有等的理由。
              判据必须是 `isLoading` 而**不是** `isSuccess` —— sys-config 挂掉时 isSuccess 永远 false，
              用它会把整个后台内容区永久卡成骨架；isLoading 在重试耗尽后转 false，放行并回落
              `~/lib/format` 的内置兜底表。
              代价：冷启动 / 整页刷新时内容区多等一个 RTT（sys-config 两个并行 GET），且页面自身的
              query 要等它落地才起飞。会话内路由切换无感 —— 本订阅常驻，该 query 不被 GC。 */}
          {sysConfigLoading ? <PageSkeleton page={current} /> : <Outlet />}
        </div>
      </main>

      {switcherOpen && (
        <AppSwitcher
          apps={apps}
          current={appId}
          onSelect={setAppId}
          onClose={() => setSwitcherOpen(false)}
        />
      )}

      {tweaksOpen && (
        <TweaksPanel
          theme={theme}
          density={density}
          accent={accent}
          sidebarCollapsed={sidebarCollapsed}
          setTweak={setTweak}
          onClose={() => {
            setTweaksOpen(false);
            try {
              window.parent.postMessage({ type: '__edit_mode_dismissed' }, '*');
            } catch {
              /* 顶层窗口无父级，忽略 */
            }
          }}
        />
      )}
    </div>
  );
}

export default AppShell;
